/**
 * dlq-clarity.js — Puente del slider hacia Microsoft Clarity (v7.2.27)
 *
 * Clarity se carga en la tienda vía Google Tag Manager (proyecto bzeksteryn),
 * NO por este plugin. Este módulo solo ENRIQUECE la sesión de Clarity con lo
 * que pasa en el slider, para poder responder desde el dashboard "qué card
 * tiene más actividad" (y ver las grabaciones de esas sesiones) sin depender
 * de la tabla rec_events:
 *
 *   Eventos (Filters → Events; Dashboard; Recordings):
 *     dlq_view_<card>     la card pasa a ser la activa (centrada): la 1ª al
 *                         cargar y cada una a la que el usuario llega. UNA vez
 *                         por card y pageview.
 *     dlq_swipe           primer swipe horizontal real del usuario en el
 *                         slider (touch o rueda). UNA vez por pageview.
 *     dlq_click_<card>    click en un tile/botón de la card.
 *     dlq_atc_<card>      add-to-cart atribuido a la card (dlq-event-bus).
 *     dlq_purchase_<card> compra atribuida.
 *   Tags (Filters → Custom tags; se acumulan por sesión):
 *     dlq_leader   card que lideró el slider (rv, cart, iseo, nv, …)
 *     dlq_history  yes|no — el visitante tenía "Vistos" con datos
 *     dlq_ab       bucket A/B del plugin (A|B)
 *     dlq_clicked  cards clicadas
 *     dlq_version  versión del plugin
 *   upgrade('dlq_slider_click'): las sesiones con click en el slider se
 *   priorizan para grabación si Clarity muestrea.
 *
 * <card> = rv | cart | ti | cv | nv | bs | iseo | antipanico | cortafuego |
 *          control_acceso | wins  (ids dlq-<x>-card / data-static-id).
 *
 * Robustez: si Clarity aún no cargó (GTM es async y suele llegar después de
 * nuestros defer), se crea el MISMO stub de cola que usa el snippet oficial
 * (window.clarity.q); clarity.js la drena al cargar. Si Clarity no existe en
 * la página, las llamadas quedan en la cola y no pasa nada. Nunca lanza.
 * Solo se carga en la home (donde vive el slider). Desactivable en Ajustes
 * (enable_clarity).
 *
 * Fuentes: dlq:telemetry (dlq-telemetry.js track), dlq:leader
 * (dlq-card-priority.js), dlq:card-active (slider-autoplay.js). Al iniciar
 * lee el estado ya publicado (data-dlq-leader, .dlq-card-active) por si esos
 * eventos se dispararon antes de que corriera este script.
 *
 * Depuración: window.dlqClarity.calls (últimas 50 llamadas enviadas).
 */
(function () {
    'use strict';

    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (window.dlqClarity && window.dlqClarity._installed) return;

    var cfg = window.dlqClarityConfig || {};
    if (cfg.enabled === false) return;

    var MAX_LOG = 50;
    var calls = [];        // ring buffer de depuración
    var viewed = {};       // cards ya reportadas como activas en este pageview
    var swiped = false;
    var lastLeader = null;

    // Stub de cola idéntico al del snippet oficial de Clarity. Si clarity.js
    // llega después, procesa window.clarity.q. Si ya existe, se respeta.
    function ensureStub() {
        if (typeof window.clarity === 'function') return;
        window.clarity = function () {
            (window.clarity.q = window.clarity.q || []).push(arguments);
        };
    }

    function send() {
        try {
            ensureStub();
            window.clarity.apply(window, arguments);
            if (calls.length >= MAX_LOG) calls.shift();
            calls.push(Array.prototype.slice.call(arguments));
        } catch (e) { /* nunca romper la página */ }
    }

    var CARD_BY_RECTYPE = {
        recently_viewed: 'rv',
        cart: 'cart',
        para_ti: 'ti',
        novedades: 'nv',
        bestsellers: 'bs'
    };

    function slug(v) {
        return String(v || '').toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);
    }

    // Card a partir de un item de telemetría (rec_type + rec_pid).
    function cardFromItem(item) {
        if (!item) return '';
        var t = String(item.rec_type || '');
        if (CARD_BY_RECTYPE[t]) return CARD_BY_RECTYPE[t];
        if (t.indexOf('covi') === 0 || t.indexOf('co_vi') === 0) return 'cv';
        if (t === 'static_promo') {
            var pid = String(item.rec_pid || '');
            return pid === 'distribuidor_iseo' ? 'iseo' : slug(pid);
        }
        return slug(t);
    }

    // Card a partir del id de la card (dlq-rv-card → rv) o data-static-id.
    function cardFromId(id) {
        if (!id) return '';
        var m = /^dlq-([a-z]+)-card$/.exec(id);
        if (m) return m[1];
        return id === 'distribuidor_iseo' ? 'iseo' : slug(id);
    }

    function cardFromElement(el) {
        if (!el) return '';
        return cardFromId(el.id || el.getAttribute('data-static-id') || '');
    }

    function onView(card) {
        if (!card || viewed[card]) return;
        viewed[card] = true;
        send('event', 'dlq_view_' + card);
    }

    function onLeader(card) {
        if (!card || card === lastLeader) return;
        lastLeader = card;
        send('set', 'dlq_leader', card);
    }

    function onSwipe() {
        if (swiped) return;
        swiped = true;
        send('event', 'dlq_swipe');
    }

    function onTelemetry(detail) {
        if (!detail || !detail.items || !detail.items.length) return;
        var type = String(detail.eventType || '');
        // Las impresiones ya van a rec_events; en Clarity la "vista" es
        // dlq_view_* (card realmente centrada), mucho más fiel.
        if (type === 'impression') return;
        var card = cardFromItem(detail.items[0]);
        if (!card) return;
        if (type === 'click') {
            send('event', 'dlq_click_' + card);
            send('set', 'dlq_clicked', card);
            send('upgrade', 'dlq_slider_click');
        } else if (type === 'add_to_cart') {
            send('event', 'dlq_atc_' + card);
        } else if (type === 'purchase') {
            send('event', 'dlq_purchase_' + card);
        }
    }

    // Swipe real: gesto horizontal del usuario sobre el slider (no cuenta el
    // scroll vertical de la página que empieza sobre el slider, ni los
    // re-snaps del navegador ni el reset de dlq-card-priority).
    function watchSwipes(slider) {
        var x0 = null, y0 = null;
        slider.addEventListener('touchstart', function (ev) {
            var t = ev.touches && ev.touches[0];
            x0 = t ? t.clientX : null;
            y0 = t ? t.clientY : null;
        }, { passive: true });
        slider.addEventListener('touchmove', function (ev) {
            if (swiped || x0 === null) return;
            var t = ev.touches && ev.touches[0];
            if (!t) return;
            var dx = t.clientX - x0, dy = t.clientY - y0;
            if (Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy)) onSwipe();
        }, { passive: true });
        slider.addEventListener('wheel', function (ev) {
            if (!swiped && Math.abs(ev.deltaX) > Math.abs(ev.deltaY) && Math.abs(ev.deltaX) > 4) onSwipe();
        }, { passive: true });
    }

    function listen(name, handler) {
        document.addEventListener(name, function (ev) {
            try { handler(ev.detail || {}); } catch (e) { /* silencio */ }
        }, false);
    }

    function init() {
        var slider = document.querySelector('.dlq-slider');
        if (!slider) return;

        // Contexto de sesión.
        send('set', 'dlq_version', String(cfg.version || ''));
        var ab = null;
        try { ab = window.dlqAb && window.dlqAb.bucket ? window.dlqAb.bucket() : null; } catch (e) {}
        if (ab) send('set', 'dlq_ab', String(ab));
        var rv = document.getElementById('dlq-rv-card');
        send('set', 'dlq_history', rv && window.getComputedStyle(rv).display !== 'none' ? 'yes' : 'no');

        // Estado ya publicado antes de que corriera este script.
        onLeader(cardFromId(slider.getAttribute('data-dlq-leader') || ''));
        var active = slider.querySelector('.dlq-card-active');
        if (active) onView(cardFromElement(active));

        listen('dlq:leader', function (d) { onLeader(cardFromId(d.id || '')); });
        listen('dlq:card-active', function (d) { onView(cardFromId(d.id || '')); });
        listen('dlq:telemetry', onTelemetry);
        watchSwipes(slider);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    window.dlqClarity = { calls: calls, _installed: true, _version: '7.2.27' };
})();
